import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateFacture,
  useUpdateFacture,
  type FactureRecouvrementRow,
  type RecouvrementStatut,
} from '@/hooks/queries/useRecouvrementEntreprises';
import { useTiers, useUpsertTier } from '@/hooks/queries/useTiers';
import { useFormations } from '@/hooks/queries/useFormation';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  activiteId: string | null;
  facture?: FactureRecouvrementRow | null;
}

const TVA_RATE = 0.19;

function formatNumberInput(n: number | ''): string {
  if (n === '' || n === 0) return '';
  return n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 });
}

function parseNumberInput(s: string): number {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

function computeStatut(ttc: number, encaisse: number): RecouvrementStatut {
  if (encaisse <= 0) return 'a_encaisser';
  if (encaisse >= ttc) return 'regle';
  return 'partiel';
}

const STATUT_LABEL: Record<RecouvrementStatut, string> = {
  a_encaisser: 'À encaisser',
  partiel: 'Partiel',
  regle: 'Réglé',
};

const LIEUX_DEFAUTS = [
  'Hôtel Mercure Alger',
  'Bay Diab',
  'Kost Academy',
  'Chez le client',
  'Autre',
];

export function CreateFactureRecouvrementDialog({
  open,
  onOpenChange,
  entrepriseId,
  activiteId,
  facture,
}: Props): JSX.Element {
  const isEdit = !!facture;
  const create = useCreateFacture();
  const update = useUpdateFacture();
  const upsertTier = useUpsertTier();
  const { data: tiers = [] } = useTiers({ entrepriseId, type: 'client_b2b', search: '' });
  const { data: formations = [] } = useFormations({ entrepriseId });
  const { data: users = [] } = useUsersList();

  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const [referenceFacture, setReferenceFacture] = useState('');
  const [entrepriseClienteNom, setEntrepriseClienteNom] = useState('');
  const [tierId, setTierId] = useState<string>('');
  const [formationTitre, setFormationTitre] = useState('');
  const [formationId, setFormationId] = useState<string>('');
  const [dateFormation, setDateFormation] = useState('');
  const [lieu, setLieu] = useState('');
  const [participants, setParticipants] = useState<number | ''>('');
  const [montantHt, setMontantHt] = useState<number | ''>('');
  const [montantHtDisplay, setMontantHtDisplay] = useState('');
  const [appliqueTva, setAppliqueTva] = useState(true);
  const [montantTva, setMontantTva] = useState<number | ''>('');
  const [montantTtc, setMontantTtc] = useState<number | ''>('');
  const [montantEncaisse, setMontantEncaisse] = useState<number | ''>('');
  const [montantEncaisseDisplay, setMontantEncaisseDisplay] = useState('');
  const [commercialId, setCommercialId] = useState('');
  const [observation, setObservation] = useState('');

  // Réf auto-suggestion : FACT-{annee}-{NNNN} si nouvelle facture
  useEffect(() => {
    if (!open || isEdit) return;
    if (!referenceFacture) {
      const rnd = String(Math.floor(Math.random() * 9000 + 1000)).padStart(4, '0');
      setReferenceFacture(`FACT-${annee}-${rnd}`);
    }
  }, [open, isEdit, referenceFacture, annee]);

  // Auto-calcul TVA + TTC depuis HT
  useEffect(() => {
    const ht = typeof montantHt === 'number' ? montantHt : 0;
    if (ht === 0) {
      setMontantTva('');
      setMontantTtc('');
      return;
    }
    if (appliqueTva) {
      const tva = Math.round(ht * TVA_RATE);
      setMontantTva(tva);
      setMontantTtc(ht + tva);
    } else {
      setMontantTva(0);
      setMontantTtc(ht);
    }
  }, [montantHt, appliqueTva]);

  useEffect(() => {
    if (!open) return;
    if (facture) {
      setAnnee(facture.annee);
      setReferenceFacture(facture.reference_facture);
      setEntrepriseClienteNom(facture.entreprise_cliente_nom);
      setTierId(facture.tier_id ?? '');
      setFormationTitre(facture.formation_titre ?? '');
      setFormationId(facture.formation_id ?? '');
      setDateFormation(facture.date_formation ?? '');
      setLieu(facture.lieu ?? '');
      setParticipants(facture.participants);
      setMontantHt(facture.montant_ht);
      setMontantHtDisplay(formatNumberInput(facture.montant_ht));
      setMontantTva(facture.montant_tva);
      setMontantTtc(facture.montant_ttc);
      setAppliqueTva(facture.montant_tva > 0);
      setMontantEncaisse(facture.montant_encaisse);
      setMontantEncaisseDisplay(formatNumberInput(facture.montant_encaisse));
      setCommercialId(facture.commercial_id ?? '');
      setObservation(facture.observation ?? '');
    } else {
      setAnnee(new Date().getFullYear());
      setReferenceFacture('');
      setEntrepriseClienteNom('');
      setTierId('');
      setFormationTitre('');
      setFormationId('');
      setDateFormation('');
      setLieu('');
      setParticipants('');
      setMontantHt('');
      setMontantHtDisplay('');
      setAppliqueTva(true);
      setMontantTva('');
      setMontantTtc('');
      setMontantEncaisse('');
      setMontantEncaisseDisplay('');
      setCommercialId('');
      setObservation('');
    }
  }, [open, facture]);

  // Auto-resolve tier
  useEffect(() => {
    const match = tiers.find(
      (t) => t.raison_sociale.trim().toLowerCase() === entrepriseClienteNom.trim().toLowerCase(),
    );
    if (match) setTierId(match.id);
    else if (tierId && match === undefined) setTierId('');
  }, [entrepriseClienteNom, tiers, tierId]);

  useEffect(() => {
    const match = formations.find(
      (f) => f.titre.trim().toLowerCase() === formationTitre.trim().toLowerCase(),
    );
    if (match) setFormationId(match.id);
    else if (formationId) setFormationId('');
  }, [formationTitre, formations, formationId]);

  const tiersList = useMemo(
    () => tiers.slice().sort((a, b) => a.raison_sociale.localeCompare(b.raison_sociale)),
    [tiers],
  );
  const formationsList = useMemo(
    () => formations.slice().sort((a, b) => a.titre.localeCompare(b.titre)),
    [formations],
  );

  const ht_n = typeof montantHt === 'number' ? montantHt : 0;
  const tva_n = typeof montantTva === 'number' ? montantTva : 0;
  const ttc_n = typeof montantTtc === 'number' ? montantTtc : 0;
  const encaisse_n = typeof montantEncaisse === 'number' ? montantEncaisse : 0;
  const statutCalc = computeStatut(ttc_n, encaisse_n);

  const submit = async (): Promise<void> => {
    if (!entrepriseClienteNom.trim()) {
      toast.error('Entreprise cliente obligatoire');
      return;
    }
    if (!referenceFacture.trim()) {
      toast.error('Référence facture obligatoire');
      return;
    }
    if (ht_n <= 0) {
      toast.error('Montant HT > 0 obligatoire');
      return;
    }
    if (encaisse_n > ttc_n) {
      toast.error('Montant encaissé ne peut excéder le TTC');
      return;
    }
    try {
      let resolvedTierId = tierId;
      if (!resolvedTierId) {
        const created = await upsertTier.mutateAsync({
          entreprise_id: entrepriseId,
          type: 'client_b2b',
          raison_sociale: entrepriseClienteNom.trim(),
          activite_principale_id: activiteId,
        } as never);
        resolvedTierId = created.id;
        toast.success(`Nouveau client B2B créé : ${entrepriseClienteNom.trim()}`);
      }

      let resolvedFormationId = formationId;
      if (!resolvedFormationId && formationTitre.trim()) {
        const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
        const { data: createdF } = await supabase
          .from('formations')
          .insert({
            entreprise_id: entrepriseId,
            code,
            titre: formationTitre.trim(),
          } as never)
          .select('id')
          .single();
        resolvedFormationId = (createdF as { id: string } | null)?.id ?? '';
      }

      if (isEdit && facture) {
        await update.mutateAsync({
          id: facture.id,
          annee,
          tier_id: resolvedTierId,
          entreprise_cliente_nom: entrepriseClienteNom.trim(),
          reference_facture: referenceFacture.trim(),
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_formation: dateFormation || null,
          lieu: lieu.trim() || null,
          participants: typeof participants === 'number' ? participants : 0,
          montant_ht: ht_n,
          montant_tva: tva_n,
          montant_ttc: ttc_n,
          montant_encaisse: encaisse_n,
          statut: statutCalc,
          commercial_id: commercialId || null,
          observation: observation.trim() || null,
        });
        toast.success('Facture modifiée');
      } else {
        await create.mutateAsync({
          entreprise_id: entrepriseId,
          activite_id: activiteId,
          tier_id: resolvedTierId,
          entreprise_cliente_nom: entrepriseClienteNom.trim(),
          annee,
          reference_facture: referenceFacture.trim(),
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_formation: dateFormation || null,
          lieu: lieu.trim() || null,
          participants: typeof participants === 'number' ? participants : 0,
          montant_ht: ht_n,
          montant_tva: tva_n,
          montant_ttc: ttc_n,
          montant_encaisse: encaisse_n,
          statut: statutCalc,
          date_reglement: null,
          mode_reglement: null,
          reference_virement: null,
          commercial_id: commercialId || null,
          observation: observation.trim() || null,
        });
        toast.success('Facture créée');
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  const isSubmitting = create.isPending || update.isPending || upsertTier.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col p-0">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier la facture' : 'Nouvelle facture B2B'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Année" required>
              <input
                type="number"
                value={annee}
                onChange={(e) => setAnnee(Number(e.target.value))}
                className="input"
                min={2000}
                max={2100}
                required
              />
            </Field>
            <Field label="Référence facture" required>
              <input
                value={referenceFacture}
                onChange={(e) => setReferenceFacture(e.target.value)}
                className="input font-mono"
                placeholder="FACT-2026-0001"
                required
              />
            </Field>
            <Field label="Date formation">
              <input
                type="date"
                value={dateFormation}
                onChange={(e) => setDateFormation(e.target.value)}
                className="input"
              />
            </Field>
          </div>

          <Field
            label="Entreprise cliente"
            required
            hint={
              tierId
                ? '✓ Client existant lié'
                : entrepriseClienteNom.trim()
                  ? '⚠ Nouveau client B2B — sera créé automatiquement'
                  : 'Tapez ou choisissez dans la liste'
            }
            warn={!tierId && !!entrepriseClienteNom.trim()}
          >
            <input
              list="clients-b2b-list"
              value={entrepriseClienteNom}
              onChange={(e) => setEntrepriseClienteNom(e.target.value)}
              className="input"
              placeholder="Sonatrach SPA, DP World…"
              required
            />
            <datalist id="clients-b2b-list">
              {tiersList.map((t) => (
                <option key={t.id} value={t.raison_sociale} />
              ))}
            </datalist>
          </Field>

          <Field
            label="Formation (optionnel)"
            hint={
              formationId
                ? '✓ Formation existante liée'
                : formationTitre.trim()
                  ? '⚠ Nouvelle formation — sera créée automatiquement'
                  : undefined
            }
            warn={!formationId && !!formationTitre.trim()}
          >
            <input
              list="formations-list"
              value={formationTitre}
              onChange={(e) => setFormationTitre(e.target.value)}
              className="input"
              placeholder="Titre de la formation"
            />
            <datalist id="formations-list">
              {formationsList.map((f) => (
                <option key={f.id} value={f.titre} />
              ))}
            </datalist>
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Lieu">
              <input
                list="lieux-list"
                value={lieu}
                onChange={(e) => setLieu(e.target.value)}
                className="input"
                placeholder="Hôtel Mercure Alger…"
              />
              <datalist id="lieux-list">
                {LIEUX_DEFAUTS.map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
            </Field>
            <Field label="Participants">
              <input
                type="number"
                value={participants}
                onChange={(e) =>
                  setParticipants(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="input tabular-nums"
                min={0}
                placeholder="0"
              />
            </Field>
            <Field label="Commercial(e)">
              <select
                value={commercialId}
                onChange={(e) => setCommercialId(e.target.value)}
                className="input"
              >
                <option value="">— Aucun —</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.full_name ?? u.user_id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Montant HT (DA)" required>
              <input
                value={montantHtDisplay}
                onChange={(e) => {
                  const n = parseNumberInput(e.target.value);
                  setMontantHt(n === 0 ? '' : n);
                  setMontantHtDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
                }}
                onBlur={() => montantHt !== '' && setMontantHtDisplay(formatNumberInput(montantHt))}
                className="input tabular-nums"
                placeholder="600 000"
                inputMode="numeric"
                required
              />
              <label className="mt-1 flex items-center gap-1 text-[11px] text-slate-600">
                <input
                  type="checkbox"
                  checked={appliqueTva}
                  onChange={(e) => setAppliqueTva(e.target.checked)}
                  className="h-3 w-3"
                />
                Appliquer TVA 19 %
              </label>
            </Field>
            <Field label="TVA (auto)" hint="Calculée à 19 % du HT">
              <input
                value={tva_n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 })}
                readOnly
                className="input bg-slate-50 tabular-nums text-slate-600"
              />
            </Field>
            <Field label="TTC (auto)" hint="HT + TVA">
              <input
                value={ttc_n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 })}
                readOnly
                className="input bg-indigo-50 font-bold tabular-nums text-indigo-900"
              />
            </Field>
          </div>

          <Field
            label="Montant déjà encaissé (DA)"
            hint={
              encaisse_n === 0
                ? 'Statut : À encaisser'
                : encaisse_n >= ttc_n
                  ? '✓ Statut : Réglé (intégralement)'
                  : `⚠ Statut : Partiel — reste ${(ttc_n - encaisse_n).toLocaleString('fr-DZ')} DA`
            }
            warn={encaisse_n > 0 && encaisse_n < ttc_n}
          >
            <input
              value={montantEncaisseDisplay}
              onChange={(e) => {
                const n = parseNumberInput(e.target.value);
                setMontantEncaisse(n === 0 ? '' : n);
                setMontantEncaisseDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
              }}
              onBlur={() =>
                montantEncaisse !== '' &&
                setMontantEncaisseDisplay(formatNumberInput(montantEncaisse))
              }
              className="input tabular-nums"
              placeholder="0"
              inputMode="numeric"
            />
          </Field>

          <Field label="Observation">
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              className="input min-h-[60px]"
              rows={2}
            />
          </Field>

          <div
            className={cn(
              'rounded-lg border px-3 py-2 text-xs',
              statutCalc === 'regle'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : statutCalc === 'partiel'
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-slate-200 bg-slate-50 text-slate-700',
            )}
          >
            <strong>Statut calculé :</strong> {STATUT_LABEL[statutCalc]}
            {!isEdit && statutCalc !== 'a_encaisser' && (
              <span className="block text-[11px] opacity-80">
                Note : la création n&apos;émet PAS d&apos;opération Caisse. Utilisez{' '}
                <em>Enregistrer encaissement</em> dans le drawer pour générer une opération ENTRÉE.
              </span>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {isEdit ? 'Enregistrer' : 'Créer la facture'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
